import React, { useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  Speed as SpeedIcon,
  Download as DownloadIcon,
  CloudQueue as CloudQueueIcon,
  SupportAgent as SupportIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api, auth } from '../services/auth';
import toast from 'react-hot-toast';

const Premium: React.FC = () => {
  const navigate = useNavigate();
  const user = auth.getCurrentUser();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const plans = [
    {
      id: 'monthly',
      name: 'Monthly Premium',
      price: '$9.99',
      period: 'per month',
      priceId: 'price_1TGjbvQeazc4Tp3zNQACMFTi',
      features: [
        'Unlimited processing',
        'Faster speed',
        'Better quality',
        'Cancel anytime',
      ],
      icon: <StarIcon sx={{ fontSize: 40, color: '#f39c12' }} />,
      popular: true,
    },
    {
      id: 'yearly',
      name: 'Yearly Premium',
      price: '$49.99',
      period: 'per year',
      priceId: 'price_1TGjdYQeazc4Tp3zOtj9oaix',
      features: [
        'Save 58%',
        'Unlimited usage',
        'Priority processing',
        'Cancel anytime',
      ],
      icon: <SpeedIcon sx={{ fontSize: 40, color: '#00b894' }} />,
      popular: false,
    },
    {
      id: 'lifetime',
      name: 'Lifetime Access',
      price: '$99.99',
      period: 'one-time payment',
      priceId: 'price_1TGjeMQeazc4Tp3zMZsz9YVZ',
      features: [
        'Everything in Premium',
        'Pay once, use forever',
        'Future updates included',
        'Priority support',
        'Best value',
      ],
      icon: <CloudQueueIcon sx={{ fontSize: 40, color: '#667eea' }} />,
      popular: false,
    },
  ];

  const handleUpgrade = async (plan: any) => {
      
    if (selectedPlan) return;

    setSelectedPlan(plan.id);
    toast.success('Opening secure checkout...', { id: 'payment' });

    try {
      const response = await api.post('/api/stripe/create-checkout-session', {
        price_id: plan.priceId,
      });

      const url = response.data?.url;

      if (!url) {
        throw new Error("No checkout URL returned");
      }

      window.location.href = url;

    } catch (error: any) {
      toast.error(
        error.response?.data?.detail || error.message || 'Payment failed',
        { id: 'payment' }
      );
      setSelectedPlan(null);
    }
  };

  const features = [
    {
      icon: <DownloadIcon />,
      title: 'Unlimited Downloads',
      description: 'Download as many videos as you want, no daily limits',
    },
    {
      icon: <SpeedIcon />,
      title: 'Faster Speeds',
      description: 'Priority processing for premium users',
    },
    {
      icon: <SupportIcon />,
      title: 'Priority Support',
      description: 'Get help within 24 hours',
    },
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Upgrade to Premium
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" paragraph sx={{ mb: 4 }}>
          Get unlimited downloads and unlock all features
        </Typography>

        {user?.is_premium && (
          <Alert severity="success" sx={{ mb: 4 }}>
            You are already a premium member! Thank you for your support.
          </Alert>
        )}

        <Grid container spacing={4} sx={{ mb: 6 }}>
          {plans.map((plan) => (
            <Grid item xs={12} md={4} key={plan.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                  ...(plan.popular && {
                    border: '2px solid #667eea',
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)',
                  }),
                }}
              >
                {plan.popular && (
                  <Chip
                    label="Most Popular"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: -12,
                      right: 24,
                      fontWeight: 'bold',
                    }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    {plan.icon}
                    <Typography variant="h5" component="h2" sx={{ mt: 2, fontWeight: 'bold' }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="h3" component="div" sx={{ mt: 2, fontWeight: 'bold' }}>
                      {plan.price}
                      <Typography component="span" variant="body2" color="text.secondary">
                        /{plan.period}
                      </Typography>
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <List dense>
                    {plan.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    fullWidth
                    variant={plan.popular ? 'contained' : 'outlined'}
                    size="large"
                    onClick={() => handleUpgrade(plan)}
                    disabled={user?.is_premium || selectedPlan === plan.id}
                    sx={{ mt: 3 }}
                  >
                    {user?.is_premium ? 'Already Premium' : 'Upgrade Now'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper sx={{ p: 4, mt: 4 }}>
          <Typography variant="h6" gutterBottom align="center">
            Why Go Premium?
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {features.map((feature, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: '#667eea', mb: 2 }}>{feature.icon}</Box>
                  <Typography variant="h6" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

      </Box>
    </Container>
  );
};

export default Premium;